import _ from 'lodash';

/**
 * Display label for a division / contest reference that may point at a
 * SOFT-DELETED entity ("tombstone").
 *
 * Deleting a division or contest is a mongoose-delete soft delete with **no
 * cascade**, so `registrationTeam.division` / `.requestedDivision` (and the
 * contest equivalents) keep pointing at the deleted document. The endpoints
 * that opt into `withDeleted` populate resolve those refs to the real document
 * — carrying `deleted: true` — instead of returning `null` and rendering a
 * blank. Suffix them so the name isn't read as current. See ADR 0028.
 *
 * The suffix is a parameter because the two audiences need different words:
 * admins get "(deleted)", which is accurate about the mechanism; registrants
 * get "(no longer available)", since "deleted" is internal vocabulary and they
 * only care that the division they asked for isn't an option any more.
 *
 * Returns '' when there is nothing to label, so callers can keep using
 * `|| '—'` fallbacks.
 */
export const entityLabel = (entity: any, deletedSuffix: string = '(deleted)'): string => {
  const name = _.get(entity, 'name');
  if (!name) return '';
  return isDeletedEntity(entity) ? `${name} ${deletedSuffix}` : name;
};

/**
 * A soft-deleted division/contest may be DISPLAYED as a row's current value,
 * but must never be selectable as a move target — and must never drive pricing
 * or capacity. Those consumers guard on this explicitly.
 */
export const isDeletedEntity = (entity: any): boolean => !!_.get(entity, 'deleted');

export default entityLabel;
